import React from "react";
import { onScrollToDiv } from "features/Common/utils/common.utils";
import { REFERRAL_BANNER_CONSTANTS } from "./ReferralBanner.constants";
import { useReferralBanner } from "./useReferralBanner";
import {
  ReferralBannerDecorativeRings,
  ArrowDownIcon,
} from "./ReferralBannerIcons";

export const ReferralBanner = ({ referralOverview, referralsList }) => {
  const {
    TITLE,
    BUTTON_TEXT,
    CREATOR_STORIES_BUTTON_TEXT,
    CREATOR_STORIES_TARGET_ID,
    TARGET_SECTION_ID,
    COMMISSION_CARD,
    FRIENDS_CARD,
  } = REFERRAL_BANNER_CONSTANTS;

  const { commissionRate, avatars, friendsJoinedText } = useReferralBanner({
    referralOverview,
    referralsList,
  });

  const handleScrollToHowItWorks = () => {
    onScrollToDiv({
      elementId: TARGET_SECTION_ID,
      block: "start",
    });
  };

  const handleScrollToCreatorStories = () => {
    onScrollToDiv({
      elementId: CREATOR_STORIES_TARGET_ID,
      block: "start",
    });
  };

  return (
    <div className="tw-relative tw-overflow-hidden tw-bg-[linear-gradient(135deg,#6C5CE7_0%,#4F3FD1_100%)] tw-text-white tw-py-[clamp(36px,6vw,64px)] tw-px-5 sm:tw-px-10 tw-mb-6">
      {/* Background Decorative Circular Lines */}
      <div
        className="tw-pointer-events-none tw-absolute tw-right-[-40px] tw-top-1/2 tw--translate-y-1/2 tw-w-[340px] tw-h-[340px] md:tw-w-[420px] md:tw-h-[420px] tw-opacity-30"
        aria-hidden="true"
      >
        <ReferralBannerDecorativeRings />
      </div>

      {/* Grid: Left Column (Text & CTAs) + Right Column (Visual Cards) */}
      <div className="tw-relative tw-z-10 tw-grid tw-grid-cols-1 lg:tw-grid-cols-12 tw-gap-8 lg:tw-gap-10 tw-items-center">
        {/* Left Column */}
        <div className="lg:tw-col-span-7 tw-max-w-xl">
          <h1 className="tw-text-3xl sm:tw-text-4xl tw-font-bold tw-leading-tight tw-tracking-tight tw-text-white tw-mb-3">
            {TITLE}
          </h1>

          <p className="tw-text-sm sm:tw-text-base tw-leading-relaxed tw-text-white/90 tw-font-normal tw-mb-6">
            Earn FLAT {commissionRate}% commission on referrals — Help the right
            people discover Exly. Share what works, support their growth, and
            earn from your referrals.
          </p>

          <div className="tw-mt-6 tw-flex tw-flex-wrap tw-items-center tw-gap-3">
            {/* Primary CTA: How it works */}
            <button
              type="button"
              onClick={handleScrollToHowItWorks}
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-bg-white tw-text-[#5B51D8] tw-font-bold tw-text-sm tw-px-5 tw-py-2.5 tw-rounded-xl tw-shadow-sm hover:tw-bg-gray-50 hover:tw-text-[#4d44bf] tw-transition-colors tw-border-0 tw-cursor-pointer"
            >
              <span>{BUTTON_TEXT}</span>
              <ArrowDownIcon />
            </button>

            {/* Secondary CTA: See creator stories */}
            <button
              type="button"
              onClick={handleScrollToCreatorStories}
              className="tw-inline-flex tw-items-center tw-justify-center tw-bg-transparent tw-text-white tw-border tw-border-solid tw-border-white/50 hover:tw-border-white hover:tw-bg-white/10 tw-font-bold tw-text-sm tw-px-5 tw-py-2.5 tw-rounded-xl tw-transition-colors tw-cursor-pointer"
            >
              {CREATOR_STORIES_BUTTON_TEXT}
            </button>
          </div>
        </div>

        {/* Right Column: Visual Graphic Cards */}
        <div className="lg:tw-col-span-5 tw-flex tw-flex-col tw-gap-3.5 tw-w-full tw-max-w-md tw-mx-auto lg:tw-mx-0">
          {/* Card 1: Commission Credited */}
          <div className="tw-bg-white tw-text-slate-900 tw-rounded-2xl tw-p-3.5 sm:tw-p-4 tw-shadow-lg tw-flex tw-items-center tw-justify-between">
            <div className="tw-flex tw-items-center tw-gap-3">
              <div className="tw-w-10 tw-h-10 tw-rounded-xl tw-bg-[#D2F9E3] tw-text-[#0FA958] tw-flex tw-items-center tw-justify-center tw-font-bold tw-text-lg tw-flex-shrink-0">
                {COMMISSION_CARD.CURRENCY_SYMBOL}
              </div>
              <div>
                <span className="tw-text-xs tw-text-slate-500 tw-block tw-leading-tight">
                  {COMMISSION_CARD.LABEL}
                </span>
                <span className="tw-text-lg sm:tw-text-xl tw-font-extrabold tw-text-[#0FA958] tw-tracking-tight tw-block">
                  {COMMISSION_CARD.AMOUNT}
                </span>
              </div>
            </div>

            <div className="tw-text-right tw-text-[11px] sm:tw-text-xs tw-text-slate-400 tw-font-medium tw-leading-tight">
              <div>{COMMISSION_CARD.PLAN}</div>
              <div>{COMMISSION_CARD.CYCLE}</div>
            </div>
          </div>

          {/* Card 2: Friends joined badge */}
          <div className="tw-bg-white tw-text-slate-900 tw-rounded-2xl tw-p-3.5 sm:tw-p-4 tw-shadow-lg tw-flex tw-items-center tw-justify-between">
            <div className="tw-flex tw-items-center tw-gap-3">
              {avatars.length > 0 && (
                <div className="tw-flex tw--space-x-2">
                  {avatars.map((avatar) => (
                    <div
                      key={avatar.id}
                      className={`tw-w-7 tw-h-7 tw-rounded-full tw-border-2 tw-border-solid tw-border-white tw-flex tw-items-center tw-justify-center tw-text-[10px] tw-font-bold ${avatar.colorClass}`}
                    >
                      {avatar.initials}
                    </div>
                  ))}
                </div>
              )}
              <span className="tw-text-xs sm:tw-text-sm tw-font-bold tw-text-slate-800">
                {friendsJoinedText}
              </span>
            </div>

            <span className="tw-text-xs sm:tw-text-sm tw-font-bold tw-text-[#4F3FD1]">
              {commissionRate}
              {FRIENDS_CARD.PERCENT_SYMBOL}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
